<template>
  <table class="table b-table" :class="tableClasses">
    <thead>
      <tr>
        <th
          v-for="field in normalizedFields"
          :key="field.slotName"
          :class="field.thClass"
          @click="handleHeaderClick(field)"
        >
          <div class="app-table-header">
            <span>{{ field.label }}</span>
            <span
              v-if="field.sortable && showSortIndicator(field)"
              class="app-table-sort-indicator"
              :class="{
                active: isSortedField(field),
                desc: currentSortDesc,
                asc: isSortedField(field) && !currentSortDesc
              }"
            >
              <span class="sort-up">&#8593;</span>
              <span class="sort-down">&#8595;</span>
            </span>
          </div>
        </th>
      </tr>
    </thead>
    <tbody :class="tbodyClass">
      <tr v-if="showEmpty && pagedItems.length === 0">
        <td :colspan="normalizedFields.length">
          <slot name="empty">
            <div class="text-center my-2">There are no records to show</div>
          </slot>
        </td>
      </tr>
      <tr
        v-for="(item, index) in pagedItems"
        v-else
        :key="getRowKey(item, index)"
        :class="getRowClasses(item)"
      >
        <td
          v-for="field in normalizedFields"
          :key="`${getRowKey(item, index)}-${field.slotName}`"
          :class="field.tdClass"
        >
          <slot
            :name="`cell(${field.slotName})`"
            v-bind="buildRowContext(item, index, field)"
          >
            <slot
              :name="field.slotName"
              v-bind="buildRowContext(item, index, field)"
            >
              {{ formatCellValue(item, field) }}
            </slot>
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
export default {
  name: 'AppTable',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    fields: {
      type: [Array, Object],
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    perPage: {
      type: Number,
      default: 0
    },
    filter: {
      type: [String, Number],
      default: ''
    },
    sortBy: {
      type: String,
      default: null
    },
    sortDesc: {
      type: Boolean,
      default: false
    },
    tbodyClass: {
      type: String,
      default: ''
    },
    primaryKey: {
      type: String,
      default: ''
    },
    noLocalSorting: {
      type: Boolean,
      default: false
    },
    striped: {
      type: Boolean,
      default: false
    },
    hover: {
      type: Boolean,
      default: false
    },
    showEmpty: {
      type: Boolean,
      default: false
    }
  },
  emits: ['filtered', 'sort-changed', 'update:sortBy', 'update:sortDesc'],
  computed: {
    normalizedFields () {
      if (Array.isArray(this.fields)) {
        return this.fields.map(field => ({
          ...field,
          slotName: field.key,
          dataKey: field.key
        }));
      }

      return Object.keys(this.fields || {}).map(slotName => {
        const field = this.fields[slotName] || {};
        return {
          ...field,
          slotName,
          dataKey: field.key || slotName
        };
      });
    },
    currentSortBy () {
      return this.sortBy;
    },
    currentSortDesc () {
      return this.sortDesc;
    },
    filteredItems () {
      const search = String(this.filter || '').trim().toLowerCase();

      if (!search) {
        return this.items.slice();
      }

      return this.items.filter(item => this.normalizedFields.some(field => {
        const value = this.formatCellValue(item, field);
        return String(value == null ? '' : value).toLowerCase().includes(search);
      }));
    },
    sortedItems () {
      if (!this.currentSortBy || this.noLocalSorting) {
        return this.filteredItems;
      }

      const sortField = this.normalizedFields.find(field =>
        field.dataKey === this.currentSortBy || field.slotName === this.currentSortBy
      );

      if (!sortField) {
        return this.filteredItems;
      }

      const direction = this.currentSortDesc ? -1 : 1;

      return this.filteredItems.slice().sort((left, right) => {
        const leftValue = this.getComparableValue(left, sortField);
        const rightValue = this.getComparableValue(right, sortField);

        if (leftValue < rightValue) {
          return -1 * direction;
        }
        if (leftValue > rightValue) {
          return 1 * direction;
        }
        return 0;
      });
    },
    pagedItems () {
      if (!this.perPage || this.perPage <= 0) {
        return this.sortedItems;
      }

      const page = this.currentPage > 0 ? this.currentPage : 1;
      const start = (page - 1) * this.perPage;
      return this.sortedItems.slice(start, start + this.perPage);
    },
    tableClasses () {
      return {
        'table-striped': this.striped,
        'table-hover': this.hover
      };
    }
  },
  watch: {
    filteredItems: {
      immediate: true,
      deep: true,
      handler (value) {
        this.$emit('filtered', value);
      }
    }
  },
  methods: {
    buildRowContext (item, index, field) {
      return {
        item,
        index,
        field,
        value: item ? item[field.dataKey] : undefined,
        detailsShowing: false,
        toggleDetails: () => {}
      };
    },
    getRowKey (item, index) {
      if (this.primaryKey && item && item[this.primaryKey] != null) {
        return item[this.primaryKey];
      }
      if (item && item.id != null) {
        return item.id;
      }
      return index;
    },
    getRowClasses (item) {
      if (!item || !item._rowVariant) {
        return [];
      }

      return [`table-${item._rowVariant}`];
    },
    formatCellValue (item, field) {
      if (!item) {
        return '';
      }

      const value = item[field.dataKey];

      if (typeof field.formatter === 'function') {
        return field.formatter(value, field.dataKey, item);
      }

      return value;
    },
    getComparableValue (item, field) {
      const value = this.formatCellValue(item, field);

      if (typeof value === 'number') {
        return value;
      }

      return String(value == null ? '' : value).toLowerCase();
    },
    isSortedField (field) {
      return this.currentSortBy === field.dataKey || this.currentSortBy === field.slotName;
    },
    showSortIndicator (field) {
      return field.showSortIndicator !== false;
    },
    handleHeaderClick (field) {
      if (!field.sortable) {
        return;
      }

      const nextSortBy = field.dataKey;
      const nextSortDesc = this.currentSortBy === nextSortBy ? !this.currentSortDesc : false;

      this.$emit('update:sortBy', nextSortBy);
      this.$emit('update:sortDesc', nextSortDesc);
      this.$emit('sort-changed', {
        sortBy: nextSortBy,
        sortDesc: nextSortDesc
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.app-table-header {
  align-items: center;
  display: flex;
  gap: 8px;
}

.app-table-sort-indicator {
  align-items: center;
  color: #c6d0d7;
  display: inline-flex;
  gap: 1px;
  margin-left: 4px;
  font-size: 13px;
  line-height: 1;
}

.app-table-sort-indicator.active {
  color: #aebbc4;
}

.app-table-sort-indicator .sort-up,
.app-table-sort-indicator .sort-down {
  opacity: 0.55;
}

.app-table-sort-indicator.asc .sort-up,
.app-table-sort-indicator.desc .sort-down {
  color: #6f7f89;
  opacity: 1;
}
</style>
